import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBan,
  FaBirthdayCake,
  FaCheckCircle,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaEnvelope,
  FaHistory,
  FaInfoCircle,
  FaMagic,
  FaPaperPlane,
  FaSearch,
  FaSyncAlt,
  FaUndo,
  FaUsers,
} from "react-icons/fa";
import api from "../api";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";
import {
  buildCrmEmailContacts,
  normalizeMarketingRecipients,
} from "./crmEmailMarketingUtils";
import CRMEmailMarketingContacts from "./CRMEmailMarketingContacts";
import CRMEmailMarketingSchedules from "./CRMEmailMarketingSchedules";
import CRMEmailMarketingBirthdays from "./CRMEmailMarketingBirthdays";
import MarketingEmailEditor from "./MarketingEmailEditor";

function Hint({ text }) {
  return (
    <span className="group relative inline-flex">
      <FaInfoCircle className="cursor-help text-slate-400 transition group-hover:text-blue-600" />
      <span className="pointer-events-none absolute right-0 top-6 z-40 hidden w-72 rounded-xl bg-slate-950 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-xl group-hover:block">
        {text}
      </span>
    </span>
  );
}

const STATUS = {
  OPENED: { label: "Aberto", className: "bg-emerald-50 text-emerald-700" },
  SENT: { label: "Entregue (SMTP)", className: "bg-blue-50 text-blue-700" },
  FAILED: { label: "Voltou/Falhou", className: "bg-rose-50 text-rose-700" },
  SUPPRESSED: { label: "Cancelado", className: "bg-slate-100 text-slate-600" },
  PENDING: { label: "Enviando", className: "bg-amber-50 text-amber-700" },
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pt-BR");
};

const STYLE_PRESETS = [
  ["Profissional", "Tom profissional, direto, elegante e objetivo."],
  ["Comercial", "Tom comercial consultivo, persuasivo sem pressão e com CTA claro."],
  ["Convite", "Tom acolhedor de convite, curto e com foco em ação."],
  ["Informativo", "Tom informativo, escaneável, com pontos principais bem destacados."],
];

export default function CRMEmailMarketing({ deals = [] }) {
  const crmContacts = useMemo(() => buildCrmEmailContacts(deals), [deals]);
  const [marketingContacts, setMarketingContacts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const contacts = useMemo(() => {
    const byEmail = new Map();
    marketingContacts.filter((item) => item.active !== false).forEach((item) => {
      const email = String(item.email || "").trim().toLowerCase();
      if (email) byEmail.set(email, { ...item, email, sourceLabel: "Cadastro" });
    });
    crmContacts.forEach((item) => {
      const email = String(item.email || "").trim().toLowerCase();
      if (!email) return;
      const existing = byEmail.get(email);
      byEmail.set(email, existing ? {
        ...item,
        ...existing,
        name: existing.name || item.name,
        company: existing.company || item.company,
        segment: existing.segment || item.segment,
      } : { ...item, sourceLabel: "CRM" });
    });
    return Array.from(byEmail.values()).sort((a, b) =>
      String(a.company || a.name || a.email).localeCompare(String(b.company || b.name || b.email), "pt-BR")
    );
  }, [crmContacts, marketingContacts]);
  const [view, setView] = useState("compose");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [theme, setTheme] = useState("");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [html, setHtml] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [stylePreset, setStylePreset] = useState("Profissional");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deliveries, setDeliveries] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [scheduleAt, setScheduleAt] = useState("");

  const loadTracking = useCallback(async () => {
    try {
      const [deliveriesResponse, suppressionsResponse, contactsResponse, schedulesResponse] = await Promise.all([
        api.get("/mail/marketing/deliveries?limit=1000"),
        api.get("/mail/marketing/suppressions?limit=1000"),
        api.get("/mail/marketing/contacts"),
        api.get("/mail/marketing/schedules"),
      ]);
      setDeliveries(Array.isArray(deliveriesResponse.data?.deliveries) ? deliveriesResponse.data.deliveries : []);
      setSuppressions(Array.isArray(suppressionsResponse.data?.suppressions) ? suppressionsResponse.data.suppressions : []);
      setMarketingContacts(Array.isArray(contactsResponse.data?.contacts) ? contactsResponse.data.contacts : []);
      setSchedules(Array.isArray(schedulesResponse.data?.schedules) ? schedulesResponse.data.schedules : []);
    } catch {
      // A criação da campanha continua utilizável mesmo se dados auxiliares estiverem indisponíveis.
    }
  }, []);

  useEffect(() => { loadTracking(); }, [loadTracking]);

  const suppressedEmails = useMemo(
    () => new Set(suppressions.map((item) => String(item.email || "").toLowerCase())),
    [suppressions],
  );

  useEffect(() => {
    setSelected((current) => current.filter((email) => !suppressedEmails.has(email)));
  }, [suppressedEmails]);

  const filtered = useMemo(() => {
    const available = contacts.filter(
      (contact) => !suppressedEmails.has(String(contact.email || "").toLowerCase()),
    );
    const term = query.trim().toLocaleLowerCase("pt-BR");
    if (!term) return available;
    return available.filter((contact) =>
      [contact.name, contact.company, contact.email, contact.segment]
        .join(" ")
        .toLocaleLowerCase("pt-BR")
        .includes(term),
    );
  }, [contacts, query, suppressedEmails]);

  const toggle = (email) => {
    if (suppressedEmails.has(email)) return;
    setSelected((current) =>
      current.includes(email)
        ? current.filter((item) => item !== email)
        : [...current, email],
    );
  };

  const selectVisible = () => {
    const visible = normalizeMarketingRecipients(
      filtered.filter((item) => !suppressedEmails.has(item.email)).map((item) => item.email),
    );
    const allSelected = visible.length > 0 && visible.every((email) => selected.includes(email));
    setSelected((current) => {
      if (allSelected) return current.filter((email) => !visible.includes(email));
      return Array.from(new Set([...current, ...visible]));
    });
  };

  const generate = async () => {
    if (!aiPrompt.trim()) {
      setError("Descreva o e-mail que a Nash IA deve criar.");
      return;
    }
    setBusy("ai");
    setError("");
    setSuccess("");
    try {
      const audience = contacts
        .filter((contact) => selected.includes(contact.email))
        .map(({ company, segment }) => ({ company, segment }));
      const styleInstruction = STYLE_PRESETS.find(([name]) => name === stylePreset)?.[1] || "";
      const response = await api.post("/mail/marketing/generate", {
        prompt: `${aiPrompt.trim()}\n\nEstilo desejado: ${styleInstruction}`,
        audience,
      });
      const generatedSubject = response.data?.subject || "";
      setTheme(response.data?.theme || generatedSubject || aiPrompt.trim().slice(0, 120));
      setSubject(generatedSubject);
      setPreviewText(response.data?.previewText || "");
      setHtml(sanitizeDocumentTemplateHtml(response.data?.html || ""));
      setSuccess("Conteúdo gerado. Revise antes de enviar.");
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível gerar o conteúdo.");
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    const recipients = normalizeMarketingRecipients(selected);
    if (!recipients.length) {
      setError("Selecione ao menos um cliente.");
      return;
    }
    if (!subject.trim() || !sanitizeDocumentTemplateHtml(html)) {
      setError("Preencha assunto e conteúdo.");
      return;
    }
    if (!window.confirm(`Enviar este e-mail para ${recipients.length} destinatário(s)?`)) return;

    setBusy("send");
    setError("");
    setSuccess("");
    try {
      const text = String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      const response = await api.post("/mail/marketing/send", {
        to: recipients,
        theme: theme.trim() || subject.trim(),
        subject: subject.trim(),
        previewText: previewText.trim(),
        html,
        text,
      });
      const sent = Number(response.data?.sent || 0);
      const failed = Number(response.data?.failed || 0);
      const suppressed = Number(response.data?.suppressed || 0);
      setSuccess(
        [
          sent ? `${sent} enviado(s)` : "",
          failed ? `${failed} falha(s)` : "",
          suppressed ? `${suppressed} cancelado(s)` : "",
        ].filter(Boolean).join(" · ") || "Envio processado.",
      );
      await loadTracking();
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível enviar a campanha.");
    } finally {
      setBusy("");
    }
  };

  const schedule = async () => {
    const recipients = normalizeMarketingRecipients(selected);
    if (!recipients.length) {
      setError("Selecione ao menos um destinatário.");
      return;
    }
    if (!subject.trim() || !sanitizeDocumentTemplateHtml(html)) {
      setError("Preencha assunto e conteúdo.");
      return;
    }
    if (!scheduleAt) {
      setError("Informe a data e o horário do agendamento.");
      return;
    }
    const scheduledDate = new Date(scheduleAt);
    if (Number.isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= Date.now()) {
      setError("Escolha um horário futuro para o agendamento.");
      return;
    }
    setBusy("schedule");
    setError("");
    setSuccess("");
    try {
      const text = String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      await api.post("/mail/marketing/schedules", {
        to: recipients,
        theme: theme.trim() || subject.trim(),
        subject: subject.trim(),
        previewText: previewText.trim(),
        html,
        text,
        scheduledAt: scheduledDate.toISOString(),
      });
      setSuccess("Campanha agendada para " + scheduledDate.toLocaleString("pt-BR") + ".");
      setScheduleAt("");
      await loadTracking();
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível agendar a campanha.");
    } finally {
      setBusy("");
    }
  };

  const restoreSuppression = async (item) => {
    if (!item?.id) return;
    if (!window.confirm(`Reativar o recebimento de e-mail marketing para ${item.email}?`)) return;
    setBusy("restore-" + item.id);
    setError("");
    setSuccess("");
    try {
      await api.delete("/mail/marketing/suppressions/" + item.id);
      setSuccess("Recebimento reativado manualmente para " + item.email + ".");
      await loadTracking();
    } catch (err) {
      setError(err.response?.data?.error || "Não foi possível reativar o recebimento.");
    } finally {
      setBusy("");
    }
  };

  const metrics = useMemo(() => ({
    sent: deliveries.filter((item) => ["SENT", "OPENED"].includes(item.status)).length,
    opened: deliveries.filter((item) => item.status === "OPENED").length,
    failed: deliveries.filter((item) => item.status === "FAILED").length,
    suppressed: suppressions.length,
  }), [deliveries, suppressions]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] p-4 text-white shadow-sm">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 text-white shadow-sm ring-1 ring-white/10" aria-hidden="true"><FaEnvelope /></span>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-black text-white">E-mail Marketing</h2>
            <Hint text="Selecione contatos do CRM, escreva manualmente ou use a Nash IA. O sistema respeita automaticamente quem cancelou o recebimento." />
          </div>
        </div>
        <div className="flex flex-wrap rounded-2xl bg-white/10 p-1">
          <button type="button" onClick={() => setView("compose")} className={`rounded-xl px-4 py-2 text-xs font-extrabold ${view === "compose" ? "bg-white text-blue-700 shadow-sm" : "text-white/90 hover:text-white"}`}>Criar</button>
          <button type="button" onClick={() => { setView("contacts"); loadTracking(); }} className={`rounded-xl px-4 py-2 text-xs font-extrabold ${view === "contacts" ? "bg-white text-blue-700 shadow-sm" : "text-white/90 hover:text-white"}`}>Cadastros</button>
          <button type="button" onClick={() => { setView("schedules"); loadTracking(); }} className={`rounded-xl px-4 py-2 text-xs font-extrabold ${view === "schedules" ? "bg-white text-blue-700 shadow-sm" : "text-white/90 hover:text-white"}`}>Agendamentos</button>
          <button type="button" onClick={() => { setView("birthdays"); loadTracking(); }} className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-extrabold ${view === "birthdays" ? "bg-white text-blue-700 shadow-sm" : "text-white/90 hover:text-white"}`}><FaBirthdayCake /> Aniversariantes</button>
          <button type="button" onClick={() => { setView("history"); loadTracking(); }} className={`rounded-xl px-4 py-2 text-xs font-extrabold ${view === "history" ? "bg-white text-blue-700 shadow-sm" : "text-white/90 hover:text-white"}`}>Envios</button>
        </div>
      </header>

      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700"><FaCheckCircle /> {success}</div>}

      {view === "compose" ? (
        <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="relative min-w-0 flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar clientes" className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-400" />
              </div>
              <button type="button" onClick={selectVisible} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-extrabold text-slate-600">Todos</button>
            </div>

            <div className="mt-3 flex gap-2 text-[11px] font-extrabold">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600"><FaUsers className="mr-1 inline" /> {contacts.length}</span>
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">{selected.length} selecionados</span>
              {suppressions.length > 0 && <span className="rounded-full bg-rose-50 px-2.5 py-1 text-rose-600">{suppressions.length} cancelados</span>}
            </div>

            <div className="mt-3 max-h-[560px] space-y-2 overflow-y-auto pr-1">
              {filtered.map((contact) => {
                const suppressed = suppressedEmails.has(contact.email);
                return (
                  <label key={contact.email} className={`flex items-start gap-3 rounded-xl border p-3 transition ${suppressed ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-65" : selected.includes(contact.email) ? "cursor-pointer border-blue-300 bg-blue-50" : "cursor-pointer border-slate-200 hover:bg-slate-50"}`}>
                    <input type="checkbox" disabled={suppressed} checked={!suppressed && selected.includes(contact.email)} onChange={() => toggle(contact.email)} className="mt-1" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-extrabold text-slate-800">{contact.company || contact.name || contact.email}</span>
                      <span className="block truncate text-xs text-slate-500">{contact.email}</span>
                      <span className="mt-1 flex flex-wrap gap-1">
                        {contact.segment && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-400">{contact.segment}</span>}
                        {suppressed && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">Cancelou recebimento</span>}
                      </span>
                    </span>
                  </label>
                );
              })}
              {!filtered.length && <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm font-semibold text-slate-400">Nenhum e-mail de cliente no CRM.</div>}
            </div>
          </aside>

          <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 shadow-sm ring-1 ring-blue-50">
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md"><FaMagic /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <strong className="text-sm font-black text-slate-900">Nash IA · Crie sua campanha em segundos</strong>
                    <Hint text="Descreva o objetivo. A IA prepara assunto, pré-cabeçalho e conteúdo; depois você pode editar tudo antes do envio." />
                  </div>
                  <textarea rows={2} value={aiPrompt} onChange={(event) => setAiPrompt(event.target.value)} placeholder="Ex.: Crie um convite curto e elegante para apresentar nossa nova solução, com chamada para conhecer mais." className="mt-2 w-full resize-none rounded-2xl border border-blue-100 bg-white px-3.5 py-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-1.5">
                      {STYLE_PRESETS.map(([name]) => (
                        <button key={name} type="button" onClick={() => setStylePreset(name)} className={`rounded-full px-3 py-1.5 text-[11px] font-extrabold transition ${stylePreset === name ? "bg-blue-600 text-white shadow-sm" : "border border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:text-blue-700"}`}>{name}</button>
                      ))}
                    </div>
                    <button type="button" onClick={generate} disabled={busy === "ai"} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-xs font-extrabold text-white shadow-md transition hover:brightness-105 disabled:opacity-50"><FaMagic /> {busy === "ai" ? "Criando campanha..." : "Gerar com IA"}</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Tema do topo<input value={theme} onChange={(event) => setTheme(event.target.value)} maxLength={120} placeholder="Ex.: Novidades do Intelligent BPM" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
              <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Assunto<input value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={180} placeholder="Assunto do e-mail" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
              <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500 md:col-span-2">Pré-cabeçalho<input value={previewText} onChange={(event) => setPreviewText(event.target.value)} maxLength={300} placeholder="Texto de prévia" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none focus:border-blue-400" /></label>
            </div>

            <MarketingEmailEditor value={html} onChange={setHtml} />

            <div className="sticky bottom-3 z-20 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
              <button type="button" onClick={() => setPreviewOpen(true)} disabled={!html} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-extrabold text-slate-600 disabled:opacity-40"><FaEye /> Prévia</button>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500">
                  <FaClock />
                  <input type="datetime-local" value={scheduleAt} onChange={(event) => setScheduleAt(event.target.value)} className="bg-transparent text-xs text-slate-700 outline-none" />
                </label>
                <button type="button" onClick={schedule} disabled={busy === "schedule" || !selected.length || !scheduleAt} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-extrabold text-blue-700 disabled:opacity-40"><FaCalendarAlt /> {busy === "schedule" ? "Agendando..." : "Agendar"}</button>
                <button type="button" onClick={send} disabled={busy === "send" || !selected.length} className="inline-flex items-center gap-2 rounded-full bg-[#0b63ce] px-5 py-2.5 text-sm font-extrabold text-white shadow-md disabled:opacity-40"><FaPaperPlane /> {busy === "send" ? "Enviando..." : "Enviar agora"}</button>
              </div>
            </div>
          </section>
        </div>
      ) : view === "contacts" ? (
        <CRMEmailMarketingContacts
          contacts={marketingContacts}
          deliveries={deliveries}
          suppressions={suppressions}
          onReload={loadTracking}
        />
      ) : view === "schedules" ? (
        <CRMEmailMarketingSchedules schedules={schedules} onReload={loadTracking} />
      ) : view === "birthdays" ? (
        <CRMEmailMarketingBirthdays onReload={loadTracking} />
      ) : (
        <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FaHistory className="text-blue-600" />
              <h3 className="font-black text-slate-900">Controle de envios</h3>
              <Hint text="Entregue (SMTP) = o servidor aceitou a mensagem. Aberto = o pixel de rastreamento foi carregado. Voltou/Falhou = rejeição ou falha detectada. Sem webhook do provedor, não é possível confirmar entrega na caixa de entrada." />
            </div>
            <button type="button" onClick={loadTracking} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-extrabold text-slate-600"><FaSyncAlt /> Atualizar</button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-blue-50 p-4"><span className="text-xs font-bold text-blue-600">Enviados</span><strong className="mt-1 block text-2xl text-blue-900">{metrics.sent}</strong></div>
            <div className="rounded-2xl bg-emerald-50 p-4"><span className="text-xs font-bold text-emerald-600">Abertos</span><strong className="mt-1 block text-2xl text-emerald-900">{metrics.opened}</strong></div>
            <div className="rounded-2xl bg-rose-50 p-4"><span className="text-xs font-bold text-rose-600">Falhas</span><strong className="mt-1 block text-2xl text-rose-900">{metrics.failed}</strong></div>
            <div className="rounded-2xl bg-slate-100 p-4"><span className="text-xs font-bold text-slate-600">Cancelaram</span><strong className="mt-1 block text-2xl text-slate-900">{metrics.suppressed}</strong></div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Destinatário</th><th className="px-4 py-3">Assunto</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Data</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {deliveries.map((item) => {
                  const status = STATUS[item.status] || STATUS.PENDING;
                  return <tr key={item.id}><td className="px-4 py-3 font-semibold text-slate-700">{item.email}</td><td className="max-w-[360px] truncate px-4 py-3 text-slate-600">{item.subject}</td><td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-extrabold ${status.className}`}>{status.label}</span>{item.error && <Hint text={item.error} />}</td><td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{formatDateTime(item.openedAt || item.sentAt || item.createdAt)}</td></tr>;
                })}
                {!deliveries.length && <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-400">Nenhum envio registrado.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="rounded-2xl border border-rose-100 bg-rose-50/40 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 text-sm font-black text-slate-800"><FaBan className="text-rose-500" /> Cancelamentos de recebimento</div>
                <p className="mt-1 text-xs text-slate-500">Esses e-mails não entram em campanhas, importações ou sincronizações. A volta só acontece por reativação manual.</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-extrabold text-rose-600 shadow-sm">{suppressions.length} cancelado(s)</span>
            </div>
            {suppressions.length ? (
              <div className="overflow-hidden rounded-xl border border-rose-100 bg-white">
                <div className="divide-y divide-slate-100">
                  {suppressions.slice(0, 200).map((item) => (
                    <div key={item.id || item.email} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <strong className="block truncate text-sm text-slate-800">{item.email}</strong>
                        <span className="text-xs text-slate-400">Cancelou em {formatDateTime(item.unsubscribedAt)}</span>
                      </div>
                      <button type="button" onClick={() => restoreSuppression(item)} disabled={busy === "restore-" + item.id} className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-extrabold text-blue-700 disabled:opacity-50">
                        <FaUndo /> {busy === "restore-" + item.id ? "Reativando..." : "Reativar manualmente"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-5 text-center text-sm text-slate-400">Nenhum cancelamento registrado.</div>
            )}
          </div>
        </section>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onMouseDown={() => setPreviewOpen(false)}>
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-slate-100 p-5 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" aria-label="Fechar prévia" title="Fechar prévia" onClick={() => setPreviewOpen(false)} className="absolute right-7 top-7 z-20 grid h-9 w-9 place-items-center rounded-full border border-white/30 bg-slate-950/55 text-xl font-black text-white shadow-lg transition hover:bg-slate-950">×</button>
            <div className="mx-auto overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="bg-[#071f54] px-7 py-5 pr-16 text-lg font-black text-white">{theme.trim() || subject.trim() || "Tema da campanha"}</div>
              <div className="p-8">
                <p className="text-xs font-bold text-slate-400">{previewText}</p>
                <h3 className="mt-1 text-xl font-black text-slate-900">{subject || "Sem assunto"}</h3>
                <div className="prose mt-6 max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: sanitizeDocumentTemplateHtml(html) }} />
              </div>
              <div className="border-t border-slate-100 bg-slate-50 p-5 text-center"><span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500">Cancelar recebimento</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
